import {
  AccountInfo,
  EndSessionPopupRequest,
  EndSessionRequest,
  IPublicClientApplication,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { first, get, orderBy } from 'lodash';
import moment from 'moment-timezone';
import { referrerParameter } from '../router/loginReferrer';

export const loginOriginKey = 'mnspro-cloud-login-origin';
const preferred_username = 'mnspro-cloud-preferred_username';

export const storeLatestLocation = () => {
  try {
    const search = new URLSearchParams(window.location.search);
    const referrer = search.get(referrerParameter);

    if (referrer) {
      const referrerPath = window.atob(referrer);
      localStorage.setItem(loginOriginKey, JSON.stringify(referrerPath));
    } else {
      const path = window.location.pathname;
      if (
        path.toLowerCase().endsWith('/login') ||
        path.toLowerCase().endsWith('/loginRedirect')
      ) {
        return;
      }

      localStorage.setItem(
        loginOriginKey,
        JSON.stringify(`${path}${window.location.search}`),
      );
    }
  } catch {
    // Console log here...
  }
};

export const getPreferredUsername = () => {
  return localStorage.getItem(preferred_username);
};

export const setPreferredUsername = (username: string) => {
  localStorage.setItem(preferred_username, username);
};

export const unsetPreferredUsername = () => {
  localStorage.setItem(preferred_username, '');
};

/**
 * This unsets the preferred user name
 * and triggers the msal account selection flow.
 */
export const selectAccount = (
  msal: IPublicClientApplication,
  interactionType: 'popup' | 'redirect',
) => {
  unsetPreferredUsername();
  const request: PopupRequest | RedirectRequest = {
    scopes: [],
    prompt: 'select_account',
  };
  if (interactionType === 'popup') {
    msal.loginPopup(request);
  } else {
    // in case of redirect we wan't to remember where we're coming from.
    storeLatestLocation();
    msal.loginRedirect(request);
  }
};

/**
 * This sets the preferred user name in case it was provided
 * and triggers the msal login flow.
 */
export const login = (
  msal: IPublicClientApplication,
  interactionType: 'popup' | 'redirect',
  loginHint?: string,
) => {
  let request: PopupRequest | RedirectRequest | undefined;
  if (loginHint) {
    request = { scopes: [], loginHint };
    setPreferredUsername(loginHint);
  }

  if (interactionType === 'popup') {
    msal.loginPopup(request);
  } else {
    // in case of redirect we wan't to remember where we're coming from.
    storeLatestLocation();
    msal.loginRedirect(request);
  }
};

/**
 * This will unset the preferred username and trigger
 * the msal logout flow.
 */
export const logout = (
  msal: IPublicClientApplication,
  interactionType: 'popup' | 'redirect',
  account?: AccountInfo,
) => {
  unsetPreferredUsername();
  let request: EndSessionPopupRequest | EndSessionRequest | undefined;
  if (account) {
    request = { account, logoutHint: account.username };
  }

  if (interactionType === 'popup') {
    msal.logoutPopup(request);
  } else {
    msal.logoutRedirect(request);
  }
};

export const tokenIsExpired = (account: AccountInfo) => {
  const expSeconds = get(account.idTokenClaims, 'exp', 0);
  const expDate = new Date(0);
  expDate.setUTCSeconds(expSeconds);

  return moment(expDate).isBefore();
};

export const getLatestIssuedAccount = (accounts: AccountInfo[]) =>
  first(orderAccountsByIssuedAt(accounts));

/**
 * Returns an descending list of accounts ordered by
 * the token claim "nbf", which always will be updated if
 * a token for this account will be acquired.
 *
 * Relying on the token claim "iat" is not reliable here because
 * this value will be the same until the token was invalidated.
 *
 * @param {AccountInfo[]} accounts
 */
export const orderAccountsByIssuedAt = (accounts: AccountInfo[]) =>
  orderBy(accounts, a => get(a.idTokenClaims, 'nbf', 0), 'desc');
